import { ball, body, coverBg, edge, fade, gloss, mix, n, rng, shadowInk, star, tint, type ArtPal } from './kit';

/** Deep: calm underwater scene — a jellyfish, fish, bubbles, a starfish and a shell on the sand. */
export function deepCover(P: ArtPal, p: string): string {
  const top = P.dark ? mix(P.bg, P.sky, 0.22) : tint(P.sky, 0.72);
  const mid = P.dark ? mix(P.panel, P.blue, 0.2) : tint(P.blue, 0.82);
  const bottom = P.dark ? mix(P.bg, P.blue, 0.1) : tint(P.teal, 0.82);
  const bg = coverBg(p, P, top, mid, bottom, [430, 160]);
  const sand = P.dark ? mix(P.gold, P.bg, 0.7) : tint(P.gold, 0.62);

  const fish = (x: number, y: number, s: number, c: string, id: string, flip = false) =>
    `<g transform="translate(${x} ${y}) scale(${flip ? -s : s} ${s})">` +
    `<path d="M26 0L50 -18Q54 -20 52 -14L44 0L52 14Q54 20 50 18Z" fill="url(#${p}${id})" stroke="${edge(c, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M-40 0C-40 -22 -12 -32 12 -26C24 -22 32 -10 32 0C32 10 24 22 12 26C-12 32 -40 22 -40 0Z" fill="url(#${p}${id})" stroke="${edge(c, P)}" stroke-width="2"/>` +
    `<path d="M-2 -28Q10 -44 22 -26" fill="url(#${p}${id})" stroke="${edge(c, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M4 -18Q12 0 4 18" fill="none" stroke="${edge(c, P)}" stroke-width="2" stroke-opacity=".5" stroke-linecap="round"/>` +
    `<circle cx="-22" cy="-5" r="6" fill="#FFFFFF"/><circle cx="-23.5" cy="-5.5" r="3.2" fill="${P.dark ? P.bg : P.ink}"/>` +
    gloss(-14, -16, 14, 4, -12, 0.45) +
    `</g>`;

  // bubbles (deterministic)
  const r = rng(11);
  const bubbles: string[] = [];
  for (let i = 0; i < 14; i++) {
    const x = 300 + r() * 330;
    const y = 30 + r() * 260;
    const rr = 3 + r() * 9;
    bubbles.push(
      `<circle cx="${n(x)}" cy="${n(y)}" r="${n(rr)}" fill="url(#${p}bb)" stroke="${tint(P.sky, 0.4)}" stroke-opacity=".55" stroke-width="1.2"/>` +
        `<circle cx="${n(x - rr * 0.35)}" cy="${n(y - rr * 0.35)}" r="${n(rr * 0.28)}" fill="#FFFFFF" fill-opacity=".7"/>`,
    );
  }

  // soft light rays from top-left
  const rays =
    `<path d="M250 0L330 0L520 400L380 400Z" fill="#FFFFFF" fill-opacity="${P.dark ? 0.035 : 0.18}"/>` +
    `<path d="M380 0L420 0L640 330L640 400L590 400Z" fill="#FFFFFF" fill-opacity="${P.dark ? 0.03 : 0.14}"/>`;

  const jx = 470;
  const jy = 150;
  const tentacle = (dx: number, len: number) =>
    `<path d="M${jx + dx} ${jy + 14}q${dx > 0 ? 8 : -8} ${len * 0.3} 0 ${len * 0.55}t0 ${len * 0.45}" fill="none" stroke="${tint(P.purple, 0.2)}" stroke-width="5" stroke-linecap="round" stroke-opacity=".8"/>`;

  const defs =
    `<defs>${bg.defs}` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.16) +
    body(`${p}sand`, sand, 0.25, 0.25, 0.3, 1) +
    ball(`${p}jl`, P.purple, 0.45, 0.35) +
    body(`${p}fa`, P.teal, 0.4, 0.35, 0.4, 1) +
    body(`${p}fb`, P.orange, 0.4, 0.35, 0.4, 1) +
    body(`${p}st`, P.orange, 0.35, 0.35, 0.6, 1) +
    body(`${p}sl`, P.pink, 0.4, 0.3, 0.4, 1) +
    `<radialGradient id="${p}bb" cx=".35" cy=".3" r=".8"><stop offset="0" stop-color="#FFFFFF" stop-opacity=".35"/><stop offset=".6" stop-color="${P.sky}" stop-opacity=".12"/><stop offset="1" stop-color="${P.sky}" stop-opacity=".3"/></radialGradient>` +
    `</defs>`;

  return (
    defs +
    bg.body +
    rays +
    // sand
    `<path d="M150 400C220 340 360 326 470 332C560 336 620 348 640 356V400Z" fill="url(#${p}sand)" stroke="${edge(sand, P)}" stroke-width="2"/>` +
    // seaweed
    `<path d="M600 350C586 310 616 284 600 240C590 214 604 196 612 184" fill="none" stroke="${P.green}" stroke-width="9" stroke-linecap="round"/>` +
    `<path d="M600 350C586 310 616 284 600 240" fill="none" stroke="${tint(P.green, 0.4)}" stroke-width="3" stroke-linecap="round" stroke-opacity=".6"/>` +
    // starfish + shell on the sand
    `<ellipse cx="392" cy="362" rx="42" ry="7" fill="url(#${p}sh)"/>` +
    `<polygon points="${star(390, 340, 34, 15, 5, -Math.PI / 2 + 0.2)}" fill="url(#${p}st)" stroke="${edge(P.orange, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    gloss(382, 328, 8, 4, -30, 0.5) +
    `<ellipse cx="548" cy="370" rx="36" ry="6" fill="url(#${p}sh)"/>` +
    `<path d="M548 368L516 334Q548 296 580 334Z" fill="url(#${p}sl)" stroke="${edge(P.pink, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M548 368L532 326M548 368L548 314M548 368L564 326" stroke="${edge(P.pink, P)}" stroke-width="1.6" stroke-opacity=".6"/>` +
    bubbles.join('') +
    // jellyfish (focus)
    tentacle(-26, 110) +
    tentacle(-9, 128) +
    tentacle(9, 120) +
    tentacle(26, 104) +
    `<path d="M${jx - 62} ${jy + 16}C${jx - 62} ${jy - 58} ${jx + 62} ${jy - 58} ${jx + 62} ${jy + 16}Q${jx + 46} ${jy + 6} ${jx + 31} ${jy + 16}Q${jx + 15} ${jy + 6} ${jx} ${jy + 16}Q${jx - 15} ${jy + 6} ${jx - 31} ${jy + 16}Q${jx - 46} ${jy + 6} ${jx - 62} ${jy + 16}Z" fill="url(#${p}jl)" stroke="${edge(P.purple, P)}" stroke-width="2" stroke-linejoin="round"/>` +
    gloss(jx - 26, jy - 26, 18, 8, -35, 0.5) +
    fish(560, 216, 1, P.teal, 'fa') +
    fish(356, 250, 0.72, P.orange, 'fb', true)
  );
}
